import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, Button, Table, useTable, Select } from "@repo/o37-ui";
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  Link as LinkIcon,
  AlertCircle,
  Search,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Zap,
  DollarSign
} from "lucide-react";
import { HoldingsTransaction, HoldingsSubscription, HoldingsMatchSuggestion, formatCents } from '../../types';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

interface TransactionWithSuggestions extends HoldingsTransaction {
  suggestions?: HoldingsMatchSuggestion[];
  loadingSuggestions?: boolean;
}

export default function TransactionReview() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const importId = searchParams.get('import');

  const [transactions, setTransactions] = useState<TransactionWithSuggestions[]>([]);
  const [subscriptions, setSubscriptions] = useState<HoldingsSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unmatched' | 'matched' | 'ignored' | 'pending_review'>('unmatched');
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 });
  const [matchingTxn, setMatchingTxn] = useState<string | null>(null);
  const [selectedSubscription, setSelectedSubscription] = useState('');
  const [runningSmartMatch, setRunningSmartMatch] = useState(false);
  const [updatingCost, setUpdatingCost] = useState<string | null>(null);

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    loadData();
  }, [importId]);

  const loadData = async () => {
    try {
      let url = `${API_URL}/holdings/transactions`;
      if (filter === 'unmatched') {
        url = `${API_URL}/holdings/transactions/unmatched`;
      }
      if (importId) {
        url += `?importId=${importId}`;
      }

      const [txnRes, subsRes] = await Promise.all([
        fetch(url, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        }),
        fetch(`${API_URL}/holdings/subscriptions`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        })
      ]);

      if (txnRes.ok) {
        const data = await txnRes.json();
        setTransactions(data.map((t: any) => ({
          id: t.id,
          importId: t.import_id,
          bankAccountId: t.bank_account_id,
          transactionDate: t.transaction_date,
          amountCents: t.amount_cents,
          description: t.description,
          vendorNameRaw: t.vendor_name_raw,
          vendorNameNormalized: t.vendor_name_normalized,
          category: t.category,
          isRecurring: t.is_recurring,
          matchedSubscriptionId: t.matched_subscription_id,
          matchStatus: t.match_status,
          matchConfidence: t.match_confidence,
          reviewStatus: t.review_status,
          notes: t.notes,
          expectedAmountCents: t.expected_amount_cents,
          amountVarianceCents: t.amount_variance_cents,
          classification: t.classification,
          matchedSubscription: t.holdings_subscriptions ? {
            id: t.holdings_subscriptions.id,
            vendorName: t.holdings_subscriptions.vendor_name,
            costCents: t.holdings_subscriptions.cost_cents
          } : undefined
        })));
      }

      if (subsRes.ok) {
        const data = await subsRes.json();
        setSubscriptions(data
          .filter((s: any) => s.status === 'active')
          .map((s: any) => ({
            id: s.id,
            vendorName: s.vendor_name,
            costCents: s.cost_cents,
            billingCycle: s.billing_cycle
          })));
      }
    } catch (e) {
      console.error('Failed to load data:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadSuggestions = async (transactionId: string) => {
    setTransactions(prev => prev.map(t =>
      t.id === transactionId ? { ...t, loadingSuggestions: true } : t
    ));

    try {
      const response = await fetch(`${API_URL}/holdings/transactions/${transactionId}/suggestions`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });

      if (response.ok) {
        const data = await response.json();
        setTransactions(prev => prev.map(t =>
          t.id === transactionId ? {
            ...t,
            suggestions: data.suggestions,
            loadingSuggestions: false
          } : t
        ));
      }
    } catch (e) {
      console.error('Failed to load suggestions:', e);
      setTransactions(prev => prev.map(t =>
        t.id === transactionId ? { ...t, loadingSuggestions: false } : t
      ));
    }
  };

  const runSmartMatch = async () => {
    setRunningSmartMatch(true);
    try {
      const response = await fetch(`${API_URL}/holdings/transactions/smart-match`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify({})
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Smart match complete!\n\nMatched: ${result.matched}\nPending review: ${result.pendingReview}\nAlerts created: ${result.alertsCreated}`);
        loadData();
      }
    } catch (e) {
      console.error('Failed to run smart match:', e);
    } finally {
      setRunningSmartMatch(false);
    }
  };

  const handleMatch = async (transactionId: string, subscriptionId: string) => {
    try {
      const response = await fetch(`${API_URL}/holdings/transactions/${transactionId}/match`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify({ subscriptionId })
      });

      if (response.ok) {
        setMatchingTxn(null);
        setSelectedSubscription('');
        loadData();
      }
    } catch (e) {
      console.error('Failed to match transaction:', e);
    }
  };

  const handleIgnore = async (transactionId: string) => {
    try {
      const response = await fetch(`${API_URL}/holdings/transactions/${transactionId}/ignore`, {
        method: 'POST',
        headers: {
          'x-user-id': userId,
          'x-user-role': userRole
        }
      });

      if (response.ok) {
        loadData();
      }
    } catch (e) {
      console.error('Failed to ignore transaction:', e);
    }
  };

  const handleUpdateSubscriptionCost = async (subscriptionId: string, newCostCents: number, alertId?: string) => {
    setUpdatingCost(subscriptionId);
    try {
      const response = await fetch(`${API_URL}/holdings/subscriptions/${subscriptionId}/update-cost`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify({ newCostCents, alertId })
      });

      if (response.ok) {
        loadData();
      }
    } catch (e) {
      console.error('Failed to update cost:', e);
    } finally {
      setUpdatingCost(null);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status: string, confidence?: number) => {
    switch (status) {
      case 'matched':
        return (
          <span className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            Matched
            {confidence && confidence >= 0.9 && <span className="text-emerald-500">{Math.round(confidence * 100)}%</span>}
          </span>
        );
      case 'pending_review':
        return (
          <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            Review
            {confidence && <span className="text-blue-500">{Math.round(confidence * 100)}%</span>}
          </span>
        );
      case 'ignored':
        return <span className="text-xs px-2 py-1 bg-neutral-100 text-neutral-600 rounded-full">Ignored</span>;
      case 'manual':
        return <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">Manual</span>;
      default:
        return <span className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded-full">Unmatched</span>;
    }
  };

  const getConfidenceBadge = (level: string) => {
    switch (level) {
      case 'exact':
        return <span className="text-xs px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded font-medium">Exact</span>;
      case 'high':
        return <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded font-medium">High</span>;
      case 'medium':
        return <span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-medium">Medium</span>;
      case 'low':
        return <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-medium">Low</span>;
      default:
        return null;
    }
  };

  const filteredTransactions = transactions.filter(t => {
    if (filter === 'all') return true;
    if (filter === 'unmatched') return t.matchStatus === 'unmatched';
    if (filter === 'pending_review') return t.matchStatus === 'pending_review';
    if (filter === 'matched') return t.matchStatus === 'matched' || t.matchStatus === 'manual';
    if (filter === 'ignored') return t.matchStatus === 'ignored';
    return true;
  });

  const columns = useMemo(() => [
    {
      id: "date",
      header: "Date",
      cell: ({ row }: { row: { original: TransactionWithSuggestions } }) => (
        <span className="font-medium text-neutral-900">
          {formatDate(row.original.transactionDate)}
        </span>
      )
    },
    {
      id: "description",
      header: "Description",
      cell: ({ row }: { row: { original: TransactionWithSuggestions } }) => (
        <div>
          <div className="font-medium text-neutral-900 truncate max-w-xs">
            {row.original.vendorNameRaw || row.original.description}
          </div>
          {row.original.vendorNameNormalized && row.original.vendorNameNormalized !== row.original.vendorNameRaw && (
            <div className="text-sm text-neutral-500">
              Matched: {row.original.vendorNameNormalized}
            </div>
          )}
        </div>
      )
    },
    {
      id: "amount",
      header: "Amount",
      cell: ({ row }: { row: { original: TransactionWithSuggestions } }) => {
        const txn = row.original;
        const hasVariance = txn.amountVarianceCents && txn.amountVarianceCents !== 0;

        return (
          <div>
            <span className={`font-medium ${txn.amountCents < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
              {formatCents(Math.abs(txn.amountCents))}
            </span>
            {hasVariance && txn.matchedSubscription && (
              <div className={`text-xs flex items-center gap-1 mt-0.5 ${txn.amountVarianceCents! > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                {txn.amountVarianceCents! > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {txn.amountVarianceCents! > 0 ? '+' : ''}{formatCents(txn.amountVarianceCents!)} from expected
              </div>
            )}
          </div>
        );
      }
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }: { row: { original: TransactionWithSuggestions } }) => (
        <div>
          {getStatusBadge(row.original.matchStatus, row.original.matchConfidence)}
          {row.original.matchedSubscription && (
            <div className="text-xs text-neutral-500 mt-1 flex items-center gap-1">
              <LinkIcon className="w-3 h-3" />
              {row.original.matchedSubscription.vendorName}
            </div>
          )}
        </div>
      )
    },
    {
      id: "actions",
      header: "",
      size: 280,
      cell: ({ row }: { row: { original: TransactionWithSuggestions } }) => {
        const txn = row.original;

        // Price change alert - offer to update subscription cost
        if (txn.matchStatus === 'matched' && txn.amountVarianceCents && Math.abs(txn.amountVarianceCents) > 0 && txn.matchedSubscription) {
          return (
            <div className="flex items-center gap-2">
              <div className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Price changed
              </div>
              <Button
                variant="outline"
                className="h-7 text-xs"
                onClick={() => handleUpdateSubscriptionCost(
                  txn.matchedSubscription!.id,
                  Math.abs(txn.amountCents)
                )}
                disabled={updatingCost === txn.matchedSubscription.id}
                icon={<DollarSign className="w-3 h-3" />}
                text={updatingCost === txn.matchedSubscription.id ? 'Updating...' : `Update to ${formatCents(Math.abs(txn.amountCents))}`}
              />
            </div>
          );
        }

        // Pending review - show matched subscription with confirm/reject
        if (txn.matchStatus === 'pending_review' && txn.matchedSubscription) {
          return (
            <div className="flex items-center gap-2">
              <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                Suggested: {txn.matchedSubscription.vendorName}
              </span>
              <Button
                variant="outline"
                className="h-7 text-xs text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                onClick={() => handleMatch(txn.id, txn.matchedSubscription!.id)}
                icon={<CheckCircle className="w-3 h-3" />}
                text="Confirm"
              />
              <Button
                variant="outline"
                className="h-7 text-xs text-neutral-500"
                onClick={() => handleIgnore(txn.id)}
                icon={<XCircle className="w-3 h-3" />}
                text="Reject"
              />
            </div>
          );
        }

        // Show dropdown matching UI
        if (matchingTxn === txn.id) {
          return (
            <div className="flex items-center gap-2">
              <Select
                value={selectedSubscription}
                onChange={(e) => setSelectedSubscription(e.target.value)}
                className="h-8 text-xs w-36"
              >
                <option value="">Select...</option>
                {subscriptions.map(s => (
                  <option key={s.id} value={s.id}>{s.vendorName}</option>
                ))}
              </Select>
              <Button
                variant="outline"
                className="h-8 text-xs px-2"
                onClick={() => {
                  if (selectedSubscription) {
                    handleMatch(txn.id, selectedSubscription);
                  }
                }}
                disabled={!selectedSubscription}
                icon={<CheckCircle className="w-3.5 h-3.5" />}
                text=""
              />
              <Button
                variant="outline"
                className="h-8 text-xs px-2"
                onClick={() => {
                  setMatchingTxn(null);
                  setSelectedSubscription('');
                }}
                icon={<XCircle className="w-3.5 h-3.5" />}
                text=""
              />
            </div>
          );
        }

        // Unmatched - show suggestions or actions
        if (txn.matchStatus === 'unmatched') {
          // Show suggestions if loaded
          if (txn.suggestions && txn.suggestions.length > 0) {
            const topSuggestion = txn.suggestions[0];
            return (
              <div className="flex items-center gap-2">
                <div className="text-xs bg-blue-50 px-2 py-1 rounded">
                  <span className="text-blue-600">Looks like:</span>
                  <span className="font-medium text-blue-800 ml-1">{topSuggestion.vendorName}</span>
                  {getConfidenceBadge(topSuggestion.confidenceLevel)}
                </div>
                <Button
                  variant="outline"
                  className="h-7 text-xs text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                  onClick={() => handleMatch(txn.id, topSuggestion.subscriptionId)}
                  icon={<CheckCircle className="w-3 h-3" />}
                  text="Yes"
                />
                <Button
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={() => setMatchingTxn(txn.id)}
                  text="Other"
                />
              </div>
            );
          }

          // Loading suggestions
          if (txn.loadingSuggestions) {
            return (
              <div className="flex items-center gap-2 text-neutral-500 text-xs">
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                Finding matches...
              </div>
            );
          }

          // Default unmatched actions
          return (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                className="h-8 text-xs"
                onClick={() => loadSuggestions(txn.id)}
                icon={<Zap className="w-3.5 h-3.5" />}
                text="Find Match"
              />
              <Button
                variant="outline"
                className="h-8 text-xs"
                onClick={() => setMatchingTxn(txn.id)}
                icon={<LinkIcon className="w-3.5 h-3.5" />}
                text="Manual"
              />
              <Button
                variant="outline"
                className="h-8 text-xs text-neutral-500"
                onClick={() => handleIgnore(txn.id)}
                icon={<XCircle className="w-3.5 h-3.5" />}
                text="Ignore"
              />
            </div>
          );
        }

        return null;
      }
    }
  ], [matchingTxn, selectedSubscription, subscriptions, updatingCost]);

  const { table, ...tableProps } = useTable({
    data: filteredTransactions,
    columns,
    pagination,
    onPaginationChange: setPagination,
    rowCount: filteredTransactions.length,
    resourceName: (plural) => `transaction${plural ? "s" : ""}`,
  });

  if (loading) {
    return (
      <div>
        <h1 className="text-2xl font-semibold mb-2">Review Transactions</h1>
        <p className="text-neutral-500">Loading...</p>
      </div>
    );
  }

  const unmatchedCount = transactions.filter(t => t.matchStatus === 'unmatched').length;
  const pendingReviewCount = transactions.filter(t => t.matchStatus === 'pending_review').length;

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/holdings/bank-imports')}
          className="flex items-center gap-1.5 text-neutral-500 hover:text-neutral-700 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Bank Imports</span>
        </button>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold mb-1">Review Transactions</h1>
            <p className="text-neutral-500">
              Match imported transactions to subscriptions
            </p>
          </div>
          <div className="flex items-center gap-3">
            {unmatchedCount > 0 && (
              <Button
                variant="outline"
                onClick={runSmartMatch}
                disabled={runningSmartMatch}
                icon={<Zap className={`w-4 h-4 ${runningSmartMatch ? 'animate-pulse' : ''}`} />}
                text={runningSmartMatch ? 'Matching...' : 'Smart Match All'}
              />
            )}
            {(unmatchedCount > 0 || pendingReviewCount > 0) && (
              <div className="flex items-center gap-2">
                {unmatchedCount > 0 && (
                  <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-4 py-2 rounded-lg">
                    <AlertCircle className="w-4 h-4" />
                    <span className="font-medium">{unmatchedCount} unmatched</span>
                  </div>
                )}
                {pendingReviewCount > 0 && (
                  <div className="flex items-center gap-2 text-blue-600 bg-blue-50 px-4 py-2 rounded-lg">
                    <AlertCircle className="w-4 h-4" />
                    <span className="font-medium">{pendingReviewCount} pending review</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center gap-1 p-1 bg-neutral-100 rounded-lg">
          {(['all', 'unmatched', 'pending_review', 'matched', 'ignored'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filter === f
                  ? 'bg-white text-neutral-900 shadow-sm'
                  : 'text-neutral-600 hover:text-neutral-900'
              }`}
            >
              {f === 'pending_review' ? 'Pending' : f.charAt(0).toUpperCase() + f.slice(1)}
              {f === 'unmatched' && unmatchedCount > 0 && (
                <span className="ml-1 text-xs bg-amber-500 text-white px-1.5 py-0.5 rounded-full">
                  {unmatchedCount}
                </span>
              )}
              {f === 'pending_review' && pendingReviewCount > 0 && (
                <span className="ml-1 text-xs bg-blue-500 text-white px-1.5 py-0.5 rounded-full">
                  {pendingReviewCount}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Transactions Table */}
      <Card>
        <CardContent className="p-0">
          {filteredTransactions.length === 0 ? (
            <div className="text-center py-12 text-neutral-500">
              <Search className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
              <h3 className="text-lg font-medium mb-2">No Transactions</h3>
              <p>
                {filter === 'unmatched'
                  ? 'All transactions have been reviewed!'
                  : filter === 'pending_review'
                  ? 'No transactions pending review.'
                  : 'No transactions found with this filter.'}
              </p>
            </div>
          ) : (
            <Table {...tableProps} table={table} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
